import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { useAuth } from '../state/AuthContext.jsx';
import { useToast } from '../state/ToastContext.jsx';

/**
 * Fetches a path and tracks loading and error state. `deps` behaves like the
 * dependency array of useEffect: change it and the request runs again.
 */
export const useFetch = (path, deps = []) => {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!path) {
      setState({ data: null, loading: false, error: null });
      return undefined;
    }

    const controller = new AbortController();
    setState((current) => ({ ...current, loading: true, error: null }));

    api
      .get(path, { signal: controller.signal })
      .then((data) => setState({ data, loading: false, error: null }))
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setState({ data: null, loading: false, error: error.message });
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, ...deps]);

  return { ...state, reload: useCallback(() => setNonce((value) => value + 1), []), setData: (data) => setState((current) => ({ ...current, data })) };
};

/**
 * Saving a listing. Visitors who are not signed in get pushed towards the sign-in
 * page instead of a silent failure, and the heart flips immediately so the tap
 * feels instant on a slow connection.
 */
export const useFavoriteToggle = (onChange) => {
  const { user } = useAuth();
  const toast = useToast();

  return useCallback(
    async (property) => {
      if (!user) {
        toast.info('Create a free account to save the houses you like.');
        return;
      }
      if (user.role !== 'tenant') {
        toast.info('Saving listings is a tenant feature.');
        return;
      }

      const next = !property.favorited;
      onChange?.(property.id, next);

      try {
        if (next) await api.post(`/favorites/${property.id}`);
        else await api.del(`/favorites/${property.id}`);
      } catch (error) {
        onChange?.(property.id, !next);
        toast.error(error.message);
      }
    },
    [user, toast, onChange]
  );
};

/** Keeps the document title in step with the page. */
export const useTitle = (title) => {
  useEffect(() => {
    const previous = document.title;
    document.title = title ? `${title} - Cameroon Homes` : 'Cameroon Homes';
    return () => {
      document.title = previous;
    };
  }, [title]);
};
